import { useState } from 'react';
import { Button } from '../../design-system/primitives/index.js';
import { formatRecipeCount, packRecipeCount } from './packRecipeCount.js';
import './RecipePacks.css';

const PREVIEW_ROWS = 3;

function recipeTime(recipe) {
  const time = recipe?.time || recipe?.cook_time || recipe?.total_time;
  return typeof time === 'string' ? time.trim() : '';
}

/**
 * Pack card with a paper stack of recipe names in the image slot.
 * Used on setup, Recipes Add, and the empty-kitchen Plan shelf.
 */
export default function PackCardQuietStack({ pack, added, onOpen, onAdd }) {
  const [adding, setAdding] = useState(false);
  const recipeCount = packRecipeCount(pack);
  const recipes = Array.isArray(pack?.recipes) ? pack.recipes : [];
  const named = recipes.filter((recipe) => recipe?.name);
  const preview = named.slice(0, PREVIEW_ROWS);
  const more = Math.max(0, recipeCount - preview.length);
  const countLabel = formatRecipeCount(recipeCount);

  const handleAdd = async () => {
    if (adding || !onAdd) return;
    setAdding(true);
    try {
      await onAdd(pack.id);
    } finally {
      setAdding(false);
    }
  };

  return (
    <article className="rp-quiet-card">
      <div className="rp-quiet-stack">
        <div className="rp-quiet-sheet rp-quiet-sheet--back" aria-hidden="true" />
        <div className="rp-quiet-sheet rp-quiet-sheet--middle" aria-hidden="true" />
        <div className="rp-quiet-sheet rp-quiet-sheet--front">
          {preview.length > 0 ? (
            <>
              <div className="rp-quiet-sheet__topline">
                <span className="rp-quiet-sheet__label">Inside this pack</span>
              </div>
              <ul className="rp-quiet-sheet__list">
                {preview.map((recipe, index) => {
                  const time = recipeTime(recipe);
                  return (
                    <li key={recipe.id || recipe.name} className="rp-quiet-row">
                      <span className="rp-quiet-row__number" aria-hidden="true">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="rp-quiet-row__name">{recipe.name}</span>
                      {time ? <span className="rp-quiet-row__time">{time}</span> : <span />}
                    </li>
                  );
                })}
              </ul>
              {more > 0 ? (
                <span className="rp-quiet-sheet__more">
                  +{more} more {more === 1 ? 'recipe' : 'recipes'}
                </span>
              ) : null}
            </>
          ) : (
            <p className="rp-quiet-sheet__empty">A set of recipes</p>
          )}
        </div>
      </div>

      <span className="rp-quiet-seam-count">{countLabel}</span>

      <div className="rp-quiet-body">
        <h3 className="rp-quiet-name">{pack.name}</h3>
        {pack.blurb ? <p className="rp-quiet-blurb">{pack.blurb}</p> : null}
        {pack.tag ? (
          <div className="rp-quiet-chips">
            <span className="rp-quiet-chip">{pack.tag}</span>
          </div>
        ) : null}
        <div className="rp-quiet-actions">
          <Button type="button" variant="secondary" className="rp-quiet-action" onClick={onOpen}>
            Preview
          </Button>
          {added ? (
            <Button type="button" variant="secondary" className="rp-quiet-action" disabled>
              ✓ Added
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              className="rp-quiet-action rp-quiet-action--add"
              onClick={handleAdd}
              disabled={adding}
              aria-busy={adding}
            >
              {adding ? 'Adding…' : `Add ${recipeCount} recipes`}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
